import { ApiError } from "./errors";

/**
 * The parts of session renewal that need no HTTP (ADR-0011 addendum 2026-10-04, plans/v2/stay-signed-in.md
 * §5.2 and §5.4), kept beside the ONE client so they can be read and tested without axios: how a failed
 * `POST /auth/refresh` is classified, the backoff after a transient failure, the "Reconnecting…" flag the
 * shell shows, and the Web Lock that makes one renewal per browser. `client.ts` does the calling.
 *
 * The rule all of it serves: every sign-in costs an SMS, so the portal ends a session only when the api
 * says a definite "no" (a 401). A Wi-Fi drop, a deploy, a 502 or a 429 during renewal keeps the person
 * signed in and shows "Reconnecting…" instead.
 */

/** Waits after consecutive transient failures; the last one repeats (plan §5.2). */
export const RENEWAL_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000] as const;

/** The name every tab of this browser renews under (plan §5.4, "One renewal per browser"). */
export const RENEWAL_LOCK = "nacs-refresh";

/**
 * What a failed renewal means for the requests that waited on it:
 *  - `ended`      401 (`UNAUTHORIZED`, `MEMBERSHIP_INACTIVE`, `TENANT_DELETED`): this sign-in is over.
 *  - `transient`  no answer, a timeout, 408, 429 or 5xx: keep the session, wait, try again.
 *  - `refused`    any other 4xx: a client bug. Keep the session and say so; never sign anyone out for it.
 * `error` is what a waiting request fails with — for `transient`, its own blip and never the 401 that
 * triggered the renewal, so `MeGate` shows a failure card with Retry rather than going to sign-in.
 */
export class RenewalFailed extends Error {
  readonly kind: "ended" | "transient" | "refused";
  readonly error: ApiError;

  constructor(kind: RenewalFailed["kind"], error: ApiError) {
    super(error.message);
    this.name = "RenewalFailed";
    this.kind = kind;
    this.error = error;
  }
}

/** The plan §5.2 table, for the refresh call's own failure. */
export function classifyRenewalFailure(error: ApiError): RenewalFailed {
  const { status } = error;
  if (status === 401) return new RenewalFailed("ended", error);
  if (status === null || status === 408) {
    const timeout = status === 408 || error.code === "TIMEOUT";
    return new RenewalFailed(
      "transient",
      new ApiError({
        code: timeout ? "TIMEOUT" : "NETWORK",
        message: timeout ? "Request timed out" : "Network error",
        status: null,
        requestId: error.requestId,
      }),
    );
  }
  if (status === 429) {
    return new RenewalFailed(
      "transient",
      new ApiError({
        code: "RATE_LIMITED",
        message: error.message,
        status,
        details: error.details,
        requestId: error.requestId,
        retryAfterSeconds: error.retryAfterSeconds,
      }),
    );
  }
  if (status >= 500) {
    return new RenewalFailed(
      "transient",
      new ApiError({
        code: "SERVER",
        message: "The server is not answering",
        status,
        requestId: error.requestId,
      }),
    );
  }
  return new RenewalFailed(
    "refused",
    new ApiError({
      code: "SESSION_RENEWAL_REFUSED",
      message: "Your session could not be renewed",
      status,
      details: { code: error.code, message: error.message },
      requestId: error.requestId,
    }),
  );
}

/**
 * The wait after transient failures. Inside it a renewal is not attempted: the request that needed one
 * fails at once with the last transient error, so a dead connection is not hammered by every query.
 */
export class RenewalBackoff {
  private failures = 0;
  private notBefore = 0;
  private last: ApiError | null = null;

  /** The error to fail with while waiting; null when a renewal may go ahead. */
  blocked(now = Date.now()): ApiError | null {
    return this.last !== null && now < this.notBefore ? this.last : null;
  }

  /** Records a transient failure and returns how long until the next attempt; a 429's `Retry-After` wins if longer. */
  failed(error: ApiError, now = Date.now()): number {
    const step = RENEWAL_BACKOFF_MS[Math.min(this.failures, RENEWAL_BACKOFF_MS.length - 1)] ?? 60_000;
    const wait = Math.max(step, error.code === "RATE_LIMITED" ? (error.retryAfterMs ?? 0) : 0);
    this.failures += 1;
    this.notBefore = now + wait;
    this.last = error;
    return wait;
  }

  /** The network is back or the tab is visible again: the next renewal may go now, counting from the start. */
  release(): void {
    this.failures = 0;
    this.notBefore = 0;
  }

  reset(): void {
    this.release();
    this.last = null;
  }
}

// ── "Reconnecting…" ─────────────────────────────────────────────────────────────────────
let reconnecting = false;
const reconnectingListeners = new Set<() => void>();

/** On while the last renewal failed transiently; off at the next successful one (plan §5.2). */
export function isReconnecting(): boolean {
  return reconnecting;
}

/** `useSyncExternalStore`-shaped: returns the unsubscribe. */
export function subscribeReconnecting(listener: () => void): () => void {
  reconnectingListeners.add(listener);
  return () => {
    reconnectingListeners.delete(listener);
  };
}

export function setReconnecting(next: boolean): void {
  if (reconnecting === next) return;
  reconnecting = next;
  for (const listener of [...reconnectingListeners]) listener();
}

// ── One renewal per browser ─────────────────────────────────────────────────────────────
/** `navigator.locks` where the browser has it; jsdom and old browsers do not. */
function lockManager(): LockManager | null {
  if (typeof navigator === "undefined") return null;
  const { locks } = navigator as { locks?: LockManager };
  return locks && typeof locks.request === "function" ? locks : null;
}

/**
 * Runs `renew` holding the browser-wide renewal lock (plan §5.4). Tabs share one refresh cookie: if two
 * renewed at once, the second would present the token the first just replaced. When another tab holds
 * the lock, this one waits for it and then runs `afterWaiting` instead — that tab has probably renewed
 * the shared cookie already, so the caller re-tries its own request before renewing again.
 *
 * Without Web Locks it is today's single-flight per tab, and the api's 60-second retry rule (a replaced
 * token presented again gets the same successor) is the backstop.
 */
export async function withRenewalLock<T>(
  renew: () => Promise<T>,
  afterWaiting: () => Promise<T>,
): Promise<T> {
  const locks = lockManager();
  if (!locks) return renew();
  const held = Symbol("held");
  const first = await locks.request(
    RENEWAL_LOCK,
    { ifAvailable: true },
    async (lock): Promise<T | typeof held> => (lock ? renew() : held),
  );
  if (first !== held) return first;
  return locks.request(RENEWAL_LOCK, async () => afterWaiting());
}
